import { useQuery } from "@tanstack/react-query";
import { Spinner, Title3 } from "@fluentui/react-components";
import { api } from "../../api/client";
import { TicketTable } from "../../components/TicketTable";
import type { Ticket } from "../../types/models";

interface AgentDash {
  assignedOpen: number;
  awaitingMe: number;
  breached: number;
  resolved7d: number;
  tickets: Ticket[];
}

export function AgentDashboardPage() {
  const q = useQuery({
    queryKey: ["dashboard", "agent"],
    queryFn: () => api.get<AgentDash>("/api/dashboard/agent"),
  });

  if (q.isLoading) return <div style={{ padding: "40px 0", textAlign: "center" }}><Spinner label="Loading dashboard…" /></div>;
  const d = q.data!;

  return (
    <div>
      <Title3>Agent Dashboard</Title3>

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
        <StatCard
          value={d.assignedOpen}
          label="Assigned & Open"
          sub="tickets in your queue"
          color="#0078D4"
        />
        <StatCard
          value={d.awaitingMe}
          label="Awaiting User"
          sub="pending user response"
          color="#8764B8"
        />
        <StatCard
          value={d.breached}
          label="SLA Breached"
          sub="require immediate attention"
          color={d.breached > 0 ? "#D13438" : "#8A8886"}
          urgent={d.breached > 0}
        />
        <StatCard
          value={d.resolved7d}
          label="Resolved (7d)"
          sub="closed in last 7 days"
          color="#107C10"
        />
      </div>

      {/* Active tickets table */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Your active tickets</h4>
          <span style={{ fontSize: 12, color: "#8A8886" }}>{d.tickets.length} ticket{d.tickets.length !== 1 ? "s" : ""}</span>
        </div>
        <TicketTable tickets={d.tickets} showRequester editable />
      </div>
    </div>
  );
}

function StatCard({
  value, label, sub, color, urgent,
}: {
  value: number;
  label: string;
  sub: string;
  color: string;
  urgent?: boolean;
}) {
  return (
    <div style={{
      flex: "1 1 170px",
      minWidth: 170,
      background: urgent ? `${color}08` : "#FFFFFF",
      border: `1px solid ${urgent ? color + "35" : "#EDEBE9"}`,
      borderRadius: 10,
      padding: "18px 20px",
      position: "relative",
      overflow: "hidden",
    }}>
      {urgent && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: color,
          borderRadius: "10px 10px 0 0",
        }} />
      )}
      <div style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#323130", marginTop: 8 }}>{label}</div>
      <div style={{ fontSize: 11, color: "#8A8886", marginTop: 3 }}>{sub}</div>
    </div>
  );
}
